import React from "react";
import { connect } from "react-redux";

import { faGear } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import Divider from "antd/es/divider";
import Dropdown from "antd/es/dropdown";
import Switch from "antd/es/switch";
import { defineMessages, useIntl } from "react-intl";

import { MultiPin } from "@mapmycustomers/shared/types/map";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import {
  toggleDuplicateLeadsVisibility,
  toggleHiddenLeadsVisibility,
} from "@app/scene/map/store/actions";
import {
  getMapViewTool,
  getMultiPin,
  isHiddenLeadsVisible,
  isPotentialDuplicateLeadsVisible,
} from "@app/scene/map/store/selectors";
import { isLeadFinderBasic } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./Title.module.scss";

interface Props {
  basicPlan: boolean;
  multiPin?: MultiPin;
  onChangeDuplicates?: () => void;
  onChangeHidden?: () => void;
  onToggleDuplicateLeadsVisibility: () => void;
  onToggleHiddenLeadsVisibility: () => void;
  showDuplicates?: boolean;
  showHidden?: boolean;
}

const messages = defineMessages({
  dropDownTooltip: {
    id: "map.tool.leadFinder.sidebar.dropdown.tooltip",
    defaultMessage: "Tap to show result options",
    description: "Lead Finder filter Dropdown tooltip",
  },
  hideToggleTooltip: {
    id: "map.tool.leadFinder.sidebar.dropdown.hideDuplicates.tooltip",
    defaultMessage:
      "If you know a lead will never be a good fit, you can hide the result. Hidden results can always be toggled into view as needed.",
    description: "Lead Finder Dropdown Hide hidden records tooltip",
  },
  multiPin: {
    id: "map.recordsList.leadFinder.title",
    defaultMessage: "Multiple Records",
    description: "Records list for multi-pin - title",
  },
  showDuplicates: {
    id: "map.tool.leadFinder.sidebar.dropdown.showDuplicates",
    defaultMessage: "Show potential duplicates",
    description: "Lead Finder filter Dropdown Show potential duplicate records toggle title",
  },
  showHiddenResults: {
    id: "map.tool.leadFinder.sidebar.dropdown.showHidden",
    defaultMessage: "Show hidden results",
    description: "Lead Finder filter Dropdown Show hidden records toggle title",
  },
  title: {
    id: "map.tool.leadFinder.sidebar.title",
    defaultMessage: "Lead Finder",
    description: "Lead Finder's record list default title",
  },
  titleBasic: {
    id: "map.tool.leadFinder.sidebar.titleBasic",
    defaultMessage: "Lead Finder Basic",
    description: "Lead Finder Basic record list default title",
  },
});

const Title: React.FC<Props> = ({
  basicPlan,
  multiPin,
  onToggleDuplicateLeadsVisibility,
  onToggleHiddenLeadsVisibility,
  showDuplicates,
  showHidden,
}) => {
  const intl = useIntl();

  const isMultiPinMode = !!multiPin;

  return (
    <div className={styles.container}>
      <span>
        {isMultiPinMode
          ? intl.formatMessage(messages.multiPin)
          : intl.formatMessage(basicPlan ? messages.titleBasic : messages.title)}
      </span>
      <Dropdown
        dropdownRender={() => (
          <div className={styles.dropdown}>
            <div className={styles.duplicateSwitch}>
              <span className={styles.subTitle}>{intl.formatMessage(messages.showDuplicates)}</span>
              <Switch checked={showDuplicates} onClick={onToggleDuplicateLeadsVisibility} />
            </div>
            <Divider className={styles.divider} />
            <div className={styles.hiddenSwitch}>
              <div className={styles.subTitleContainer}>
                <TextWithInfo info={intl.formatMessage(messages.hideToggleTooltip)}>
                  {intl.formatMessage(messages.showHiddenResults)}
                </TextWithInfo>
              </div>
              <Switch checked={showHidden} onClick={onToggleHiddenLeadsVisibility} />
            </div>
          </div>
        )}
        trigger={["click"]}
      >
        <Tooltip title={intl.formatMessage(messages.dropDownTooltip)}>
          <FontAwesomeIcon className={styles.icon} icon={faGear} />
        </Tooltip>
      </Dropdown>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  basicPlan: isLeadFinderBasic(state),
  multiPin: getMultiPin(state),
  selectedTool: getMapViewTool(state),
  showDuplicates: isPotentialDuplicateLeadsVisible(state),
  showHidden: isHiddenLeadsVisible(state),
});

const mapDispatchToProps = {
  onToggleDuplicateLeadsVisibility: toggleDuplicateLeadsVisibility,
  onToggleHiddenLeadsVisibility: toggleHiddenLeadsVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(Title);
